import { useCallback, useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { parseSSEChunk } from './sse-line-parser.ts'
import { createReconnectScheduler, type ReconnectScheduler } from './sse-reconnect-scheduler.ts'

export const ConnectionStatusSchema = z.enum(['idle', 'connecting', 'connected', 'error', 'finished'])
export type ConnectionStatus = z.infer<typeof ConnectionStatusSchema>

const DEFAULT_HEADERS = {
  'X-Requested-With': 'JavaScript',
  Accept: 'text/event-stream',
} as const

type UseSSEStreamOptions<T> = {
  url: string | undefined
  schema: z.ZodType<T>
  isFinished: (event: T) => boolean
  onEvent?: (event: T) => void
  headers?: Record<string, string>
  /**
   * When provided, `data:` payloads of SSE events whose `event:` name is not in this list
   * are dropped silently (no parse attempt, no console error). Use for streams that emit
   * auxiliary named events (e.g. keep-alives) alongside the payloads this hook consumes.
   */
  eventNames?: readonly string[]
}

export type SSEStream<T> = {
  events: T[]
  connectionStatus: ConnectionStatus
  error: Error | undefined
  isEndpointAvailable: boolean
}

export const useSSEStream = <T>({
  url,
  schema,
  isFinished,
  onEvent,
  headers = DEFAULT_HEADERS,
  eventNames,
}: UseSSEStreamOptions<T>): SSEStream<T> => {
  const [events, setEvents] = useState<T[]>([])
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('idle')
  const [error, setError] = useState<Error>()
  const [isEndpointAvailable, setIsEndpointAvailable] = useState(true)

  const schedulerRef = useRef<ReconnectScheduler | null>(null)
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent
  const schemaRef = useRef(schema)
  schemaRef.current = schema
  const isFinishedRef = useRef(isFinished)
  isFinishedRef.current = isFinished
  const eventNamesRef = useRef(eventNames)
  eventNamesRef.current = eventNames

  const connect = useCallback(
    async (connectUrl: string, signal: AbortSignal) => {
      setConnectionStatus('connecting')
      setError(undefined)

      try {
        const response = await fetch(connectUrl, { headers, signal })
        if (signal.aborted) return

        if (response.status === 404) {
          setIsEndpointAvailable(false)
          setConnectionStatus('idle')
          return
        }

        if (!response.ok) {
          throw new Error(`SSE connection failed with status ${response.status}`)
        }

        if (!response.body) {
          throw new Error('SSE response has no body')
        }

        setConnectionStatus('connected')
        // The endpoint replays the full event history on every (re)connection, so
        // start each connection from an empty buffer. Otherwise a reconnect after a
        // dropped stream appends the replay onto the events from the previous
        // connection and every activity renders twice.
        setEvents([])
        schedulerRef.current?.notifySuccess()

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done || signal.aborted) break

          const chunk = decoder.decode(value, { stream: true })
          const result = parseSSEChunk(buffer, chunk, schemaRef.current, eventNamesRef.current)
          buffer = result.buffer

          if (result.events.length > 0) {
            setEvents(prev => [...prev, ...result.events])

            for (const event of result.events) {
              onEventRef.current?.(event)
            }

            const hasFinish = result.events.some(isFinishedRef.current)
            if (hasFinish) {
              setConnectionStatus('finished')
              schedulerRef.current?.notifyFinished()
              return
            }
          }
        }

        if (!signal.aborted) {
          setConnectionStatus('finished')
          schedulerRef.current?.notifyFinished()
        }
      } catch (err: unknown) {
        if (signal.aborted) return
        const caughtError = err instanceof Error ? err : new Error(String(err))
        if (schedulerRef.current) {
          schedulerRef.current.scheduleReconnect(caughtError)
        } else {
          setError(caughtError)
          setConnectionStatus('error')
        }
      }
    },
    [headers]
  )

  useEffect(() => {
    if (!url) {
      setConnectionStatus('idle')
      setEvents([])
      setError(undefined)
      return
    }

    const abortController = new AbortController()

    setEvents([])

    const scheduler = createReconnectScheduler({
      onReconnect: () => {
        if (!abortController.signal.aborted) {
          connect(url, abortController.signal)
        }
      },
      onGiveUp: () => {
        setError(new Error('SSE reconnection failed after maximum retries'))
        setConnectionStatus('error')
      },
    })

    schedulerRef.current = scheduler
    connect(url, abortController.signal)

    return () => {
      abortController.abort()
      scheduler.dispose()
    }
  }, [url, connect])

  return { events, connectionStatus, error, isEndpointAvailable }
}
